import { Page } from 'react-pdf';
import { Loader2 } from 'lucide-react';
import { PDF_CONFIG } from '@/utils/pdfConfig';
import PageFigureOverlay from './PageFigureOverlay';
import PageAnnotationsOverlay from './PageAnnotationsOverlay';
import PageNotesOverlay from './PageNotesOverlay';
import { normalizeClickToPdfCoords } from '../utils/pdfCoordinates';

export default function PDFPageStack({
  numPages,
  pageSpacingClass,
  pageJustifyClass,
  pageRefs,
  notePlacementMode,
  onPDFClick,
  pageScale,
  textLayerReadyPagesRef,
  highlightMatches,
  handlePageLoadSuccess,
  pageDimensions,
  activeFigureHighlight,
  chapterAnnotations,
  userAnnotationPositions,
  onActivityClick,
  isActivityCompleted,
  activeAnnotationId,
  chapterId,
  activeBookmarks,
  onBookmarkChange,
  saveAnnotationPosition,
  setPositionUpdateTrigger,
  positionUpdateTrigger,
  pageNotesMap,
  highlightedNoteId,
  onNoteUpdate,
  onNotePageChange,
  containerRef,
  setEditingNote,
}) {
  return Array.from(new Array(numPages), (el, index) => {
    const pageNum = index + 1;

    // Use memoized page notes map for instant lookup (O(1) instead of O(n))
    const pageNotes = pageNotesMap[pageNum] || [];

    return (
      <div
        key={`page-${pageNum}`}
        className={`${pageSpacingClass} flex ${pageJustifyClass} relative`}
        data-page-number={pageNum}
        ref={(elRef) => {
          if (elRef) {
            pageRefs.current[pageNum] = elRef;
          }
        }}
        onClick={(e) => {
          if (notePlacementMode && onPDFClick) {
            const pageContainer = pageRefs.current[pageNum];
            const pageElement = pageContainer?.querySelector('.react-pdf__Page');
            if (pageElement) {
              const rect = pageElement.getBoundingClientRect();
              const normalized = normalizeClickToPdfCoords({
                clientX: e.clientX,
                clientY: e.clientY,
                rect,
              });

              onPDFClick(pageNum, normalized);
            }
          }
        }}
        style={{
          cursor: notePlacementMode ? 'crosshair' : 'default'
        }}
      >
        <div className="relative">
          <Page
            pageNumber={pageNum}
            scale={pageScale || PDF_CONFIG.scale}
            renderTextLayer={PDF_CONFIG.renderTextLayer}
            renderAnnotationLayer={false}
            renderMode="canvas"
            imageResourcesPath="https://unpkg.com/pdfjs-dist@5.4.296/build/"
            onRenderTextLayerSuccess={() => {
              textLayerReadyPagesRef.current[pageNum] = true;
              highlightMatches(pageNum);
            }}
            onLoadSuccess={(page) => {
              handlePageLoadSuccess(page);
              const pageContainer = pageRefs.current[pageNum];
              if (pageContainer) {
                const pageElement = pageContainer.querySelector('.react-pdf__Page');
                if (pageElement) {
                  const rect = pageElement.getBoundingClientRect();
                  pageDimensions.current[pageNum] = {
                    width: rect.width,
                    height: rect.height
                  };
                }
              }
            }}
            loading={
              <div className="flex items-center justify-center w-full h-[800px] bg-white border border-gray-200 rounded">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            }
            className="shadow-lg"
          />

          <PageFigureOverlay
            activeFigureHighlight={activeFigureHighlight}
            pageNum={pageNum}
            pageRefs={pageRefs}
          />

          <PageAnnotationsOverlay
            pageNum={pageNum}
            pageRefs={pageRefs}
            chapterAnnotations={chapterAnnotations}
            userAnnotationPositions={userAnnotationPositions}
            saveAnnotationPosition={saveAnnotationPosition}
            setPositionUpdateTrigger={setPositionUpdateTrigger}
            positionUpdateTrigger={positionUpdateTrigger}
            onActivityClick={onActivityClick}
            isActivityCompleted={isActivityCompleted}
            activeAnnotationId={activeAnnotationId}
            chapterId={chapterId}
            activeBookmarks={activeBookmarks}
            onBookmarkChange={onBookmarkChange}
          />

          <PageNotesOverlay
            pageNum={pageNum}
            pageRefs={pageRefs}
            pageDimensions={pageDimensions}
            pageNotes={pageNotes}
            highlightedNoteId={highlightedNoteId}
            positionUpdateTrigger={positionUpdateTrigger}
            onNoteUpdate={onNoteUpdate}
            onNotePageChange={onNotePageChange}
            containerRef={containerRef}
            setEditingNote={setEditingNote}
            numPages={numPages}
          />
        </div>
      </div>
    );
  });
}
